
(function () {
  'use strict';

  if (globalThis.__newsletterSampleLoaded) return;
  globalThis.__newsletterSampleLoaded = true;

  // ── helpers ──────────────────────────────────────────────────────────────
  function isValidEmail(value) {
    return /^([A-Za-z0-9_\-.+])+@([A-Za-z0-9_\-.])+\.([A-Za-z]{2,})$/.test(value || '');
  }

  function setBusy(form, submitBtn, busy) {
    if (form) form.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (submitBtn) {
      if (busy) submitBtn.setAttribute('disabled', 'disabled');
      else submitBtn.removeAttribute('disabled');
    }
  }

  function showFeedbackMessage(message) {
    if (!message) return false;
    try {
      let fb = document.querySelector('feedback-bar-element');
      if (fb?.message_json && globalThis.trigger) {
        fb.message_json['newsletter--existing'] = message;
        globalThis.trigger('theme:feedback:load', { async: 'newsletter--existing' });
        return true;
      }
    } catch (e) {
      console.warn('[Newsletter Sample] showFeedbackMessage failed:', e);
    }
    return false;
  }

  // Light client-side abuse guard. Only the FIRST submit of a given email that
  // actually grants a sample records an attempt; repeats of an already-cached
  // email never reach this. Generous limits so QA can test many addresses,
  // while still capping runaway scripted abuse per session.
  // max 30 distinct-email attempts per session, 1s cooldown between attempts.
  let _rate = (function () {
    let MAX = 30, COOLDOWN = 1000, attempts = 0, last = 0;
    return {
      check: function () {
        let now = Date.now();
        if (attempts >= MAX) return false;
        if (attempts > 0 && now - last < COOLDOWN) return false;
        return true;
      },
      record: function () { attempts++; last = Date.now(); },
    };
  })();

  // ── New-vs-returning detection via a 90-day localStorage cache ────────────
  // Mirrors the Product Recommender: instead of a Storefront API token we hash
  // the email and remember (for 90 days) that this browser already received the
  // new-subscriber sample, so a returning visitor doesn't get a second one. The
  // raw email is never stored — only a SHA-256 hash (with a non-crypto fallback
  // for insecure contexts where crypto.subtle is unavailable).
  const SUBSCRIBE_TTL_MS = 7776e6; // 90 days = 90 × 24 × 60 × 60 × 1000

  async function hashEmail(email) {
    let normalized = (email || '').trim().toLowerCase();
    try {
      let bytes = new TextEncoder().encode(normalized);
      let digest = await crypto.subtle.digest('SHA-256', bytes);
      return Array.from(new Uint8Array(digest))
        .map(function (b) { return b.toString(16).padStart(2, '0'); })
        .join('');
    } catch (e) {
      console.warn('[Newsletter Sample] crypto.subtle unavailable, using fallback hash:', e);
      let h = 0;
      for (let i = 0; i < normalized.length; i++) {
        h = (h << 5) - h + (normalized.codePointAt(i) ?? 0);
        h = Math.trunc(h);
      }
      return 'f' + (h >>> 0).toString(16);
    }
  }

  function wasRecentlySubscribed(key) {
    try {
      let raw = localStorage.getItem(key);
      if (!raw) return false;
      let data = JSON.parse(raw);
      if (!data?.t) return false;
      if (Date.now() - data.t > SUBSCRIBE_TTL_MS) {
        localStorage.removeItem(key); // expired → eligible for a sample again
        return false;
      }
      return true;
    } catch (e) {
      console.warn('[Newsletter Sample] wasRecentlySubscribed error:', e);
      return false;
    }
  }

  function markSubscribed(key) {
    try { localStorage.setItem(key, JSON.stringify({ t: Date.now() })); } catch (e) {
      console.warn('[Newsletter Sample] markSubscribed error:', e);
    }
  }

  // ── Resolve SKU → letiant id via public endpoints (no auth needed) ────────
  async function getVariantIdBySku(sku) {
    let target = (sku || '').trim();
    if (!target) return null;
    try {
      let searchRes = await fetch(
        '/search/suggest.json?q=' + encodeURIComponent(target) +
        '&resources[type]=product&resources[fields]=variants.sku'
      );
      if (!searchRes.ok) return null;
      let text = await searchRes.text();
      if (!text.trimStart().startsWith('{')) return null; // password-page guard

      let json = JSON.parse(text);
      let products = json?.resources?.results?.products || [];
      for (const product of products) {
        let handleMatch = product.url?.match(/\/products\/([^/?#]+)/);
        if (!handleMatch) continue;
        let prRes = await fetch('/products/' + handleMatch[1] + '.js');
        if (!prRes.ok) continue;
        let productData = await prRes.json();
        let variants = productData.variants || [];
        let match = variants.find(function (v) {
          return (v.sku && v.sku.trim() === target) || (v.barcode && v.barcode.trim() === target);
        });
        if (match) return match.id;
      }
    } catch (e) {
      console.warn('[Newsletter Sample] getVariantIdBySku error:', e);
    }
    return null;
  }

  async function addSampleSilently(sku, variantId) {
    
    let resolvedId = (variantId && /^\d+$/.test(String(variantId))) ? String(variantId) : null;
    if (!resolvedId) {
      resolvedId = await getVariantIdBySku(sku);
    }
    if (!resolvedId) {
      console.warn('[Newsletter Sample] Could not resolve variant for SKU:', sku);
      return false;
    }
    let addUrl = (globalThis.Shopify?.routes?.cart_add || '/cart/add') + '.js';
    let res = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: Number.parseInt(resolvedId, 10), quantity: 1 }),
    });
    if (!res.ok) {
      let errText = await res.text().catch(function () { return ''; });
      console.warn('[Newsletter Sample] /cart/add.js failed:', res.status, errText);
      return false;
    }
    return true;
  }

  
  function nativeSubscribeViaIframe(form, email, label) {
    try {
      let fn = form.querySelector('input[name="contact[first_name]"]');
      let ln = form.querySelector('input[name="contact[last_name]"]');
      if (fn && email) fn.value = email;
      if (ln) ln.value = '- ' + (label || 'Newsletter Signup');
    } catch (e) {
      console.warn('[Newsletter Sample] nativeSubscribeViaIframe setup failed:', e);
    }
    let name = 'newsletter-sample-sink-' + Date.now();
    let iframe = document.createElement('iframe');
    iframe.name = name;
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('tabindex', '-1');
    iframe.style.cssText = 'position:absolute;left:-9999px;top:-9999px;width:0;height:0;border:0;';
    document.body.appendChild(iframe);

    let prevTarget = form.getAttribute('target');
    form.setAttribute('target', name); 

    let ok = true;
    try {
      form.submit(); 
    } catch (e) {
      console.warn('[Newsletter Sample] iframe subscribe failed:', e);
      ok = false;
    }

    if (prevTarget) form.setAttribute('target', prevTarget);
    else form.removeAttribute('target');
    setTimeout(function () { try { iframe.remove(); } catch (e) { console.warn('[Newsletter Sample] iframe cleanup failed:', e); } }, 6000);

    return ok;
  }

  // ── Enhance a single newsletter section ──────────────────────────────────
  function enhance(root) {
    if (!root || root.dataset.sampleBound === 'true') return;
    if (root.dataset.sampleEnabled !== 'true') return;

    let sku = root.dataset.sampleSku;
    let variantId = root.dataset.sampleVariantId;
    let subscriberLabel = root.dataset.subscriberLabel || 'Newsletter Signup';
    let alreadyMessage = root.dataset.alreadySubscribedMessage || "You're already subscribed!";
    if (!variantId && !sku) return; // not configured — leave native form untouched

    let subscribeForm = root.querySelector('subscribe-form');
    if (!subscribeForm) return;
    let form = subscribeForm.querySelector('form');
    let input = subscribeForm.querySelector('.subscribe-form--input');
    let submitBtn = subscribeForm.querySelector('button[type="submit"]') || subscribeForm.querySelector('button');
    if (!form || !input) return;

    root.dataset.sampleBound = 'true';

    async function handleSubmit() {
      let email = (input.value || '').trim();
      if (!isValidEmail(email)) {
        globalThis.trigger?.('theme:feedback:load', { async: 'email-invalid' });
        return;
      }

      setBusy(form, submitBtn, true);

      // New vs returning is decided purely by a 90-day localStorage cache keyed
      // by a hash of the email — same policy as the Product Recommender, no
      // Storefront API token required.
      let cacheKey = 'nl_sub_' + (await hashEmail(email));

      if (wasRecentlySubscribed(cacheKey)) {
        // Already received the sample on this browser within 90 days → no second
        // sample, and no need to re-submit the native form.
        setBusy(form, submitBtn, false);
        try { input.value = ''; } catch (e) { console.warn('[Newsletter Sample] Failed to clear input:', e); }
        if (!showFeedbackMessage(alreadyMessage)) {
          // Feedback bar not available → fall back to the generic success bar.
          globalThis.trigger?.('theme:feedback:load', { async: 'newsletter' });
        }
        return;
      }

      // Treated as a NEW subscriber. The session rate guard only gates the free
      // sample; the subscription itself always goes through the native form.
      if (_rate.check()) {
        _rate.record();
        // Remember (90 days) that this browser got the sample, so a rapid
        // re-submit of the same email can't double-grant.
        markSubscribed(cacheKey);
        try { await addSampleSilently(sku, variantId); } catch (e) { console.warn('[Newsletter Sample] addSampleSilently failed (non-fatal):', e); }
      }

      // Record the subscription via the theme's native Shopify form — this is
      // what actually adds the email to your customer / marketing list.
      nativeSubscribeViaIframe(form, email, subscriberLabel);

      setBusy(form, submitBtn, false);
      try { input.value = ''; } catch (e) { console.warn('[Newsletter Sample] Failed to clear input:', e); }
      globalThis.trigger?.('theme:feedback:load', { async: 'newsletter' });
    }

    
    form.addEventListener('click', function (e) {
      let btn = e.target.closest('button');
      if (!btn) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      handleSubmit();
    }, true);
  }

  function initAll() {
    let roots = document.querySelectorAll('.newsletter--root[data-sample-enabled="true"]');
    Array.prototype.forEach.call(roots, enhance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor: re-bind when a section is re-rendered.
  document.addEventListener('shopify:section:load', initAll);
})();
